/**
 * Minutes east of UTC for an IANA timezone at a given instant.
 * DST-aware. The IANA identifier itself is never rewritten.
 * Returns null when the identifier is missing or invalid.
 */
export function offsetMinutesForIana(
  ianaTimezone: string | null | undefined,
  at: Date = new Date()
): number | null {
  const zone = ianaTimezone?.trim();
  if (!zone || !isCanonicalZoneName(zone)) return null;

  try {
    const dtf = new Intl.DateTimeFormat('en-US', {
      timeZone: zone,
      timeZoneName: 'shortOffset',
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23',
    });
    const label = dtf
      .formatToParts(at)
      .find((part) => part.type === 'timeZoneName')?.value;
    if (!label) return null;
    return parseShortOffset(label);
  } catch {
    return null;
  }
}

function isCanonicalZoneName(zone: string): boolean {
  if (zone.includes('/')) return true;
  return /^(UTC|GMT)$/i.test(zone);
}

function parseShortOffset(label: string): number | null {
  const value = label.trim();
  if (/^(GMT|UTC)$/i.test(value)) return 0;

  const match = value.match(/^(?:GMT|UTC)([+-])(\d{1,2})(?::(\d{2}))?$/i);
  if (!match) return null;

  const sign = match[1] === '-' ? -1 : 1;
  const hours = Number(match[2]);
  const minutes = match[3] ? Number(match[3]) : 0;
  return sign * (hours * 60 + minutes);
}
